import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { MaxsoldLotDetailDto } from '@jordan/types';
import { Alert, Badge, Breadcrumb, Button, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';
import { maxsoldSidebar } from './maxsoldSidebar';
import { formatClose, formatMoney } from './maxsoldSearchUtils';
import { FavouriteHeart, useWatchlist } from './watchlist';
import { MaxsoldBidBox } from './MaxsoldBidBox';

/** Image gallery: a large active image with a clickable thumbnail strip. `topRight` overlays the main image. */
function Gallery({ images, title, topRight }: { images: string[]; title: string; topRight?: ReactNode }) {
  const [active, setActive] = useState(0);
  const current = images[active];
  if (images.length === 0) {
    return (
      <div className="position-relative bg-light rounded d-flex align-items-center justify-content-center" style={{ aspectRatio: '4 / 3' }}>
        <i className="ph-image ph-3x text-muted" />
        {topRight && <div className="position-absolute top-0 end-0 m-2">{topRight}</div>}
      </div>
    );
  }
  return (
    <div>
      <div className="position-relative">
        <img
          src={current}
          alt={title}
          className="w-100 rounded border"
          style={{ aspectRatio: '4 / 3', objectFit: 'contain', background: 'var(--bs-tertiary-bg, #f4f4f4)' }}
        />
        {topRight && <div className="position-absolute top-0 end-0 m-2">{topRight}</div>}
      </div>
      {images.length > 1 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setActive(i)}
              className="p-0 border rounded"
              style={{
                width: 64,
                height: 64,
                overflow: 'hidden',
                outline: i === active ? '2px solid var(--bs-primary)' : 'none',
                cursor: 'pointer',
              }}
              aria-label={`Image ${i + 1}`}
            >
              <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Where the lot was opened from — drives the back link, breadcrumb crumb, and sidebar highlight. */
const ORIGINS: Record<string, { label: string; path: string }> = {
  favourites: { label: 'Favourites', path: '/maxsold/favourites' },
  bids: { label: 'Bids', path: '/maxsold/bids' },
};

export function MaxsoldLotPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lotId } = useParams<{ lotId: string }>();
  const [searchParams] = useSearchParams();

  // Only Favourites/Bids override the default "came via Search → Auction" flow.
  const origin = useMemo(() => ORIGINS[searchParams.get('from') ?? ''] ?? null, [searchParams]);

  const [lot, setLot] = useState<MaxsoldLotDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const wl = useWatchlist();
  // Ticks once a minute so the "auction ended" state flips without a manual refresh.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!lotId) return;
    setLoading(true);
    setErr(null);
    apiGet<MaxsoldLotDetailDto>(`/api/maxsold/lot/${lotId}`)
      .then(setLot)
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not load that lot.');
        setLot(null);
      })
      .finally(() => setLoading(false));
  }, [lotId]);

  const enr = lot?.enrichment;

  return (
    <AppChrome secondarySidebar={maxsoldSidebar(navigate, pathname, origin?.path)}>
      <PageHeader
        title={
          <>
            Maxsold - <span className="fw-normal">Lot</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Maxsold' },
              // From Favourites/Bids the section crumb changes, but the auction/lot detail crumb
              // still shows: e.g. Maxsold › Favourites › <auction> › Lot.
              origin
                ? { label: origin.label, href: origin.path }
                : { label: 'Search', href: '/maxsold/search' },
              ...(lot ? [{ label: lot.auctionTitle ?? 'Auction', href: `/maxsold/auction/${lot.auctionId}` }] : []),
              { label: 'Lot' },
            ]}
          />
        }
      />

      <div className="content">
        <div className="mb-3">
          <Button
            variant="light"
            icon="ph-arrow-left"
            onClick={() =>
              origin ? navigate(origin.path) : navigate(lot ? `/maxsold/auction/${lot.auctionId}` : '/maxsold/search')
            }
          >
            {origin ? `Back to ${origin.label}` : lot ? 'Back to auction' : 'Back to search'}
          </Button>
        </div>

        {err && (
          <Alert variant="danger" icon="ph-warning">
            {err}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          lot && (
            <>
              <div className="row g-4">
                <div className="col-12 col-lg-7">
                  <Gallery
                    images={lot.images}
                    title={lot.title}
                    topRight={
                      wl.configured ? (
                        <FavouriteHeart
                          favourite={wl.isFavourite(lot.id)}
                          pending={wl.isPending(lot.id)}
                          onToggle={() => wl.toggle(lot.id)}
                          size={26}
                        />
                      ) : undefined
                    }
                  />
                </div>

                <div className="col-12 col-lg-5">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {lot.lotNumber != null && <Badge variant="dark">Lot {lot.lotNumber}</Badge>}
                        {enr?.categories.map((c) => (
                          <Badge key={c} variant="primary" soft>
                            {c}
                          </Badge>
                        ))}
                      </div>
                      <h5 className="mb-3">{lot.title}</h5>

                      <div className="d-flex align-items-baseline gap-2 mb-1">
                        <span className="fs-2 fw-bold">{formatMoney(lot.currentBid)}</span>
                        <span className="text-muted">{lot.currency}</span>
                      </div>
                      {lot.currentBid != null && lot.surchargePercent > 0 && (
                        <div className="fs-sm mb-1">
                          <span className="fw-semibold">
                            {formatMoney(Math.round(lot.currentBid * (1 + lot.surchargePercent / 100)))}
                          </span>{' '}
                          <span className="text-muted">incl. {lot.surchargePercent}% surcharge</span>
                        </div>
                      )}
                      <div className="text-muted fs-sm mb-3">
                        <i className="ph-gavel me-1" />
                        {lot.bidCount} {lot.bidCount === 1 ? 'bid' : 'bids'} · Closes {formatClose(lot.closeTime)}
                      </div>

                      {(enr?.condition || enr?.brand || enr?.working) && (
                        <dl className="row mb-3 fs-sm">
                          {enr?.brand && (
                            <>
                              <dt className="col-4 text-muted fw-normal">Brand</dt>
                              <dd className="col-8 mb-1 text-capitalize">{enr.brand}</dd>
                            </>
                          )}
                          {enr?.condition && (
                            <>
                              <dt className="col-4 text-muted fw-normal">Condition</dt>
                              <dd className="col-8 mb-1 text-capitalize">{enr.condition}</dd>
                            </>
                          )}
                          {enr?.working && (
                            <>
                              <dt className="col-4 text-muted fw-normal">Working</dt>
                              <dd className="col-8 mb-1 text-capitalize">{enr.working}</dd>
                            </>
                          )}
                        </dl>
                      )}

                      <MaxsoldBidBox
                        lotId={lot.id}
                        title={lot.title}
                        currency={lot.currency}
                        closeTime={lot.closeTime}
                        configured={wl.configured}
                        surchargePercent={lot.surchargePercent}
                        externalUrl={lot.url}
                        now={now}
                        onContext={(ctx) =>
                          setLot((prev) => (prev ? { ...prev, currentBid: ctx.currentBid ?? prev.currentBid } : prev))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Description + attributes */}
              <div className="row g-4 mt-0">
                <div className="col-12 col-lg-7">
                  {(enr?.qualitativeDescription || lot.description) && (
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">Description</h6>
                      </div>
                      <div className="card-body">
                        {enr?.qualitativeDescription && <p>{enr.qualitativeDescription}</p>}
                        {lot.description && (
                          <p className="mb-0 text-muted" style={{ whiteSpace: 'pre-line' }}>
                            {lot.description}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-5">
                  {enr && enr.attributes.length > 0 && (
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">Details</h6>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <tbody>
                            {enr.attributes.map((at) => (
                              <tr key={at.name}>
                                <th className="text-muted fw-normal text-capitalize" style={{ width: '40%' }}>
                                  {at.name.replace(/_/g, ' ')}
                                </th>
                                <td className="text-capitalize">{at.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )
        )}
      </div>
    </AppChrome>
  );
}
